import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Award, Briefcase, GraduationCap } from "lucide-mui";
import type { ReactNode } from "react";
import type { ContactProfile, ContactProfileExperience } from "../../../api/client.ts";
import Panel from "../../../components/Panel.tsx";
import { formatPeriod, formatProfileDate } from "./profileFormat.ts";

/** One line of a career, an education or a certification: icon, what, where, when. */
function Entry({
  icon,
  title,
  subtitle,
  period,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  period?: string | null;
  children?: ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
      <Box sx={{ color: "text.secondary", pt: 0.25, display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && <Typography variant="body2">{subtitle}</Typography>}
        {period && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {period}
          </Typography>
        )}
        {children}
      </Box>
    </Stack>
  );
}

function Company({ role }: { role: ContactProfileExperience }) {
  const name = role.company ?? "Unknown company";
  const site = role.company_domain
    ? `https://${role.company_domain.replace(/^https?:\/\//, "")}`
    : role.company_linkedin_url;
  return site ? (
    <Link href={site} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit">
      {name}
    </Link>
  ) : (
    <>{name}</>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={1.75}>{children}</Stack>
    </Box>
  );
}

function Chips({ values }: { values: string[] }) {
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
      {values.map((v) => (
        <Chip key={v} size="small" variant="outlined" label={v} />
      ))}
    </Stack>
  );
}

/**
 * The person's career, education, certifications, skills and languages as the
 * sources we bought from tell it. Our own work history (employment stints,
 * dated by how we know them) is the Work history card; this is the source's
 * account, shown as given.
 */
export default function ContactBackgroundCard({ profile }: { profile: ContactProfile }) {
  const sections: ReactNode[] = [];
  if (profile.experience.length) {
    sections.push(
      <Section key="career" title="Career">
        {profile.experience.map((role, i) => (
          <Entry
            key={`${role.company}-${role.title}-${role.start}-${i}`}
            icon={<Briefcase fontSize="small" />}
            title={
              <>
                {role.title ?? "Role"}
                {role.is_current && (
                  <Chip size="small" label="Current" color="success" sx={{ ml: 1 }} />
                )}
              </>
            }
            subtitle={
              <>
                <Company role={role} />
                {role.location && (
                  <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                    {" "}
                    · {role.location}
                  </Typography>
                )}
              </>
            }
            period={formatPeriod(role.start, role.end, role.is_current)}
          >
            {role.description && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {role.description}
              </Typography>
            )}
          </Entry>
        ))}
      </Section>,
    );
  }
  if (profile.education.length) {
    sections.push(
      <Section key="education" title="Education">
        {profile.education.map((e, i) => (
          <Entry
            key={`${e.school}-${i}`}
            icon={<GraduationCap fontSize="small" />}
            title={e.school}
            subtitle={[e.degree, e.field].filter(Boolean).join(", ") || undefined}
            period={formatPeriod(e.start, e.end)}
          />
        ))}
      </Section>,
    );
  }
  if (profile.certifications.length) {
    sections.push(
      <Section key="certifications" title="Certifications">
        {profile.certifications.map((c, i) => (
          <Entry
            key={`${c.name}-${i}`}
            icon={<Award fontSize="small" />}
            title={c.name}
            subtitle={c.issuer}
            period={formatProfileDate(c.issued_on)}
          />
        ))}
      </Section>,
    );
  }
  if (profile.skills.length) {
    sections.push(
      <Section key="skills" title="Skills">
        <Chips values={profile.skills} />
      </Section>,
    );
  }
  if (profile.languages.length) {
    sections.push(
      <Section key="languages" title="Languages">
        <Chips values={profile.languages} />
      </Section>,
    );
  }
  if (!sections.length) return null;
  return (
    <Panel title="Background" subtitle="Career, education and skills as the person's profile gives them" sx={{ mb: 3 }}>
      <Stack spacing={3} divider={<Divider flexItem />}>
        {sections}
      </Stack>
    </Panel>
  );
}
